import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categoryService, Category } from "../../services/categoryService";
import Footer from "../../components/layout/Footer";

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await categoryService.getAll();
      // Filter only active categories and sort by displayOrder
      const activeCategories = data
        .filter((cat) => cat.isActive !== false)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      setCategories(activeCategories);
    } catch (err: any) {
      setError(err.message || "فشل تحميل الفئات");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="categories-page bg-background-light dark:bg-background-dark min-h-screen flex flex-col">
      {/* Mobile Top App Bar */}
      <div className="md:hidden flex items-center bg-background-light dark:bg-background-dark p-4 pb-2 justify-between sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
        <h2 className="text-[#0d171b] dark:text-white text-lg font-bold leading-tight tracking-[-0.015em] flex-1 text-center">
          الفئات
        </h2>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block bg-background-light dark:bg-background-dark border-b border-slate-200 dark:border-slate-800">
        <div className="container mx-auto px-6 py-4">
          <h1 className="text-3xl font-bold text-[#0d171b] dark:text-white">
            الفئات
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-12 flex-1">
        {loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">جاري التحميل...</p>
            </div>
          </div>
        ) : error ? (
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
              <svg
                className="w-12 h-12 text-red-500 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-red-600 dark:text-red-400 font-semibold mb-2">
                حدث خطأ
              </p>
              <p className="text-red-500 dark:text-red-500 text-sm">{error}</p>
              <button
                onClick={loadCategories}
                className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
              >
                إعادة المحاولة
              </button>
            </div>
          </div>
        ) : categories.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-8 md:p-12 border border-slate-200 dark:border-slate-700">
              <svg
                className="w-16 h-16 text-slate-400 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              <h3 className="text-xl font-bold text-[#0d171b] dark:text-white mb-2">
                لا توجد فئات متاحة
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                لم يتم العثور على أي فئات في الوقت الحالي
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Categories Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/catalog?categoryId=${category.id}`}
                  className="group bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 hover:border-primary hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#0d171b] dark:text-white mb-2 group-hover:text-primary transition">
                        {category.name}
                      </h3>
                      {category.description && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                          {category.description}
                        </p>
                      )}
                    </div>
                    <svg
                      className="w-6 h-6 text-slate-400 group-hover:text-primary transition flex-shrink-0 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                  <div className="flex items-center text-primary text-sm font-medium">
                    <span>عرض المنتجات</span>
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>

            {/* Call to Action */}
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 md:p-8 border border-primary/20">
                <h2 className="text-xl md:text-2xl font-bold mb-3 text-[#0d171b] dark:text-white">
                  لم تجد ما تبحث عنه؟
                </h2>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  تصفح جميع منتجاتنا في الكاتالوج
                </p>
                <Link
                  to="/catalog"
                  className="inline-block bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary/90 transition shadow-lg shadow-primary/30"
                >
                  تصفح الكاتالوج الكامل
                </Link>
              </div>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Categories;

